import React, {
  createContext,
  useState,
  useEffect,
} from "react";

import { apiClient } from "../../services/api";
import runService from "../../services/runService";

export const ChatContext = createContext();

export const ChatProvider = ({ children }) => {
  const [chatbox, setChatbox] = useState("");
  const [chatboxStatus, setChatboxStatus] = useState("");
  const [geminiLoading, setGeminiLoading] = useState(false);
  const [chatHistory, setChatHistory] = useState([]);

  const [isOpen, setIsOpen] = useState(false);

  const [selectedModel, setSelectedModelState] = useState(
    "google/gemini-3.5-flash"
  );

  const [availableModels, setAvailableModels] =
    useState([]);

  const [runId, setRunId] = useState(null);

  const [activeCodebaseId, setActiveCodebaseId] =
    useState(null);

  const [isSwitchingModel, setIsSwitchingModel] =
    useState(false);

  /*
   * Load saved model and available models.
   */
  useEffect(() => {
    const savedModel =
      localStorage.getItem("selectedModel");

    if (savedModel) {
      setSelectedModelState(savedModel);
    }

    fetchAvailableModels();
  }, []);

  /*
   * Persist selected model preference.
   */
  useEffect(() => {
    if (selectedModel) {
      localStorage.setItem(
        "selectedModel",
        selectedModel
      );
    }
  }, [selectedModel]);

  const fetchAvailableModels = async () => {
    try {
      const response =
        await apiClient.get("/api/models");

      const models = response.data || [];

      setAvailableModels(models);

      if (models.length) {
        setSelectedModelState((current) =>
          models.some(
            (model) => model.id === current
          )
            ? current
            : models[0].id
        );
      }
    } catch (error) {
      console.error(
        "Error fetching models:",
        error
      );
    }
  };

  const getAuth = () => ({
    userId: sessionStorage.getItem("userId"),
    sessionToken:
      sessionStorage.getItem("session_token"),
  });

  const mapMessages = (messages = []) =>
    messages.map((message) => ({
      role: message.role,
      content: message.content,
    }));

  /*
   * Load the most recent run for a specific model.
   *
   * If none exists for this codebase/model combination,
   * create a new one.
   */
  const activateModelRun = async (
    codebaseId,
    modelId
  ) => {
    const { userId, sessionToken } = getAuth();

    if (
      !userId ||
      !sessionToken ||
      !codebaseId ||
      !modelId
    ) {
      return;
    }

    try {
      setIsSwitchingModel(true);

      const existingRun =
        await runService.getActiveRunForModel(
          userId,
          sessionToken,
          codebaseId,
          modelId
        );

      if (existingRun) {
        setRunId(existingRun.id);

        setChatHistory(
          mapMessages(existingRun.messages)
        );
      } else {
        const {
          runId: newRunId,
        } = await runService.startRun(
          userId,
          sessionToken,
          codebaseId,
          modelId
        );

        setRunId(newRunId);
        setChatHistory([]);
      }

      setActiveCodebaseId(codebaseId);
    } catch (error) {
      console.error(
        "Error activating model run:",
        error
      );

      setChatboxStatus(
        "Could not load conversation for this model."
      );
    } finally {
      setIsSwitchingModel(false);
    }
  };

  /*
   * Called when the editor opens.
   *
   * Instead of loading whichever run was edited last,
   * load the run associated with the currently selected
   * model.
   */
  const activateRun = async (codebaseId) => {
    if (!codebaseId) {
      return;
    }

    await activateModelRun(
      codebaseId,
      selectedModel
    );
  };

  /*
   * Explicitly load a past run from Run History.
   */
  const loadRun = async (
    runIdToLoad,
    codebaseId
  ) => {
    const { userId, sessionToken } = getAuth();

    if (
      !userId ||
      !sessionToken ||
      !runIdToLoad
    ) {
      return;
    }

    try {
      const run = await runService.getRun(
        runIdToLoad,
        userId,
        sessionToken
      );

      setRunId(run.id);

      setSelectedModelState(
        run.selected_model
      );

      setChatHistory(
        mapMessages(run.messages)
      );

      setActiveCodebaseId(codebaseId);
    } catch (error) {
      console.error(
        "Error loading run:",
        error
      );
    }
  };

  /*
   * Model selector handler.
   *
   * Every model gets its own run/conversation.
   */
  const setSelectedModel = async (
    modelId
  ) => {
    if (!modelId) {
      return;
    }

    if (modelId === selectedModel) {
      return;
    }

    setSelectedModelState(modelId);

    /*
     * If no project is active yet, only update the
     * model preference.
     */
    if (!activeCodebaseId) {
      setChatHistory([]);
      setRunId(null);
      return;
    }

    /*
     * Load/create the conversation that belongs
     * specifically to this model.
     */
    await activateModelRun(
      activeCodebaseId,
      modelId
    );
  };

  /*
   * Start a fresh conversation for the currently
   * selected model.
   */
  const resetChat = async () => {
    const { userId, sessionToken } =
      getAuth();

    if (
      !userId ||
      !sessionToken ||
      !activeCodebaseId
    ) {
      setChatHistory([]);
      setRunId(null);
      setChatbox("");

      localStorage.removeItem(
        "chatHistory"
      );

      return;
    }

    try {
      const {
        runId: newRunId,
      } = await runService.startRun(
        userId,
        sessionToken,
        activeCodebaseId,
        selectedModel
      );

      setRunId(newRunId);
      setChatHistory([]);
      setChatbox("");
    } catch (error) {
      console.error(
        "Error starting new run:",
        error
      );
    }
  };

  const handleChatbox = async () => {
    if (!chatbox.trim()) {
      setChatboxStatus(
        "Please provide your query."
      );
      return;
    }

    const { userId, sessionToken } =
      getAuth();

    if (!runId) {
      setChatboxStatus(
        "No active run. Open a codebase to start chatting."
      );
      return;
    }

    if (isSwitchingModel) {
      setChatboxStatus(
        "Please wait while the selected model conversation loads."
      );
      return;
    }

    try {
      setGeminiLoading(true);

      const userMessage =
        chatbox.trim();

      const runTime =
        localStorage.getItem("runTime");

      const messageToSend = runTime
        ? `Code runtime: ${runTime} ms\n\n${userMessage}`
        : userMessage;

      /*
       * Optimistically show the user's message.
       */
      setChatHistory((previous) => [
        ...previous,
        {
          role: "user",
          content: userMessage,
        },
      ]);

      setChatbox("");

      const data =
        await runService.sendMessage(
          runId,
          userId,
          sessionToken,
          messageToSend,
          selectedModel
        );

      setChatHistory((previous) => [
        ...previous,
        {
          role: "assistant",
          content: data.response,
        },
      ]);

      setChatboxStatus(
        "Query processed successfully!"
      );
    } catch (error) {
      console.error(
        "Error processing query:",
        error
      );

      setChatboxStatus(
        "Error processing query."
      );
    } finally {
      setGeminiLoading(false);

      setTimeout(
        () => setChatboxStatus(""),
        3000
      );
    }
  };

  return (
    <ChatContext.Provider
      value={{
        chatbox,
        setChatbox,

        chatboxStatus,

        chatHistory,
        setChatHistory,

        handleChatbox,
        resetChat,

        geminiLoading,

        isOpen,
        setIsOpen,

        selectedModel,
        setSelectedModel,

        availableModels,

        runId,
        activateRun,
        loadRun,

        isSwitchingModel,
      }}
    >
      {children}
    </ChatContext.Provider>
  );
};